import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import useViewportZoom from "@/lib/useViewportZoom";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import SchoolsList from "@/pages/SchoolsList";
import SchoolForm from "@/pages/SchoolForm";
import BudgetsList from "@/pages/BudgetsList";
import BudgetForm from "@/pages/BudgetForm";
import BudgetDetail from "@/pages/BudgetDetail";
import ServicesItems from "@/pages/ServicesItems";
import FazDepoisPaga from "@/pages/FazDepoisPaga";
import Financeiro from "@/pages/Financeiro";
import FinanceSettings from "@/pages/FinanceSettings";
import FinanceCaixa from "@/pages/FinanceCaixa";
import FinanceContaDia from "@/pages/FinanceContaDia";
import RafaelInvest from "@/pages/RafaelInvest";
import FinanceMonthly from "@/pages/FinanceMonthly";
import FinanceImpostos from "@/pages/FinanceImpostos";
import FinanceCartao from "@/pages/FinanceCartao";
import Notificacoes from "@/pages/Notificacoes";
import { SociosList, SocioReport } from "@/pages/FinanceSocios";
import Settings from "@/pages/Settings";
import Anotacoes from "@/pages/Anotacoes";
import Cronograma from "@/pages/Cronograma";
import Calendario from "@/pages/Calendario";
import MinhaConta from "@/pages/MinhaConta";
import Funcionarios from "@/pages/Funcionarios";
import Diarias from "@/pages/Diarias";
import PayrollHistory from "@/pages/PayrollHistory";
import Relatorios from "@/pages/Relatorios";
import ServiceRequests from "@/pages/ServiceRequests";
import LandingPage from "@/pages/LandingPage";
import LandingMedia from "@/pages/LandingMedia";
import Galeria from "@/pages/Galeria";
import FazDepoisPagaPublic from "@/pages/FazDepoisPagaPublic";
import PublicBudgetView from "@/pages/PublicBudgetView";
import GlobalLoadingBar from "@/components/GlobalLoadingBar";

// School portal
import InvitePage from "@/pages/school/InvitePage";
import SchoolLogin from "@/pages/school/SchoolLogin";
import SchoolForgotPassword from "@/pages/school/SchoolForgotPassword";
import SchoolResetPassword from "@/pages/school/SchoolResetPassword";
import SchoolLayout from "@/pages/school/SchoolLayout";
import SchoolDashboard from "@/pages/school/SchoolDashboard";
import SchoolBudgets from "@/pages/school/SchoolBudgets";
import SchoolBudgetDetail from "@/pages/school/SchoolBudgetDetail";
import FdpEntriesDetail from "@/pages/FdpEntriesDetail";
import SchoolNewRequest from "@/pages/school/SchoolNewRequest";
import SchoolProfile from "@/pages/school/SchoolProfile";
import SchoolFotos from "@/pages/school/SchoolFotos";
import SchoolAjuda from "@/pages/school/SchoolAjuda";
import SchoolExpired from "@/pages/school/SchoolExpired";

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#7c3aed]"></div>
  </div>
);

const RequireRole = ({ role, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) {
    return <Navigate to={role === "school" ? "/escola/login" : "/login"} replace />;
  }
  if (user.role !== role) {
    return <Navigate to={user.role === "school" ? "/escola" : "/"} replace />;
  }
  return children;
};

/**
 * HomeGate decides what to render at `/`:
 *   - not logged in     → public LandingPage (only on exact "/")
 *   - logged as school  → send to /escola
 *   - logged as admin   → render admin <Layout /> whose <Outlet /> shows the Dashboard
 *
 * For any admin sub-route (e.g. /orcamentos) a non-admin visitor is redirected
 * to /login — this is handled by the useLocation + Navigate branch below.
 */
const HomeGate = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  // Admin preview: allow logged-in users to see the public landing when
  // explicitly requested with ?preview=1 (used by the sidebar link).
  const isPreview = new URLSearchParams(location.search).get("preview") === "1";
  if (location.pathname === "/" && isPreview) return <LandingPage />;
  if (!user) {
    if (location.pathname === "/") return <LandingPage />;
    return <Navigate to="/login" replace />;
  }
  if (user.role === "school") return <Navigate to="/escola" replace />;
  return <Layout />;
};

function AppRouter() {
  const location = useLocation();
  useViewportZoom();
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/convite/:token" element={<InvitePage />} />
      <Route path="/escola/login" element={<SchoolLogin />} />
      <Route path="/escola/esqueci-senha" element={<SchoolForgotPassword />} />
      <Route path="/escola/redefinir-senha/:token" element={<SchoolResetPassword />} />
      <Route path="/galeria" element={<Galeria />} />
      <Route path="/faz-depois-paga" element={<FazDepoisPagaPublic />} />
      <Route path="/orcamento/:id" element={<PublicBudgetView />} />

      {/* School portal (role=school) */}
      <Route
        path="/escola"
        element={
          <RequireRole role="school">
            <SchoolLayout />
          </RequireRole>
        }
      >
        <Route index element={<SchoolDashboard />} />
        <Route path="orcamentos" element={<SchoolBudgets />} />
        <Route path="orcamentos/vencidos" element={<SchoolExpired />} />
        <Route path="orcamentos/:id" element={<SchoolBudgetDetail />} />
        <Route path="orcamentos/:id/lancamentos" element={<FdpEntriesDetail scope="school" />} />
        <Route path="solicitar" element={<SchoolNewRequest />} />
        <Route path="dados" element={<SchoolProfile />} />
        <Route path="fotos" element={<SchoolFotos />} />
        <Route path="ajuda" element={<SchoolAjuda />} />
      </Route>

      {/* Admin app ("/" shows public Landing if not logged in; Dashboard if admin) */}
      <Route path="/" element={<HomeGate />}>
        <Route index element={<Dashboard />} />
        <Route path="orcamentos" element={<BudgetsList />} />
        <Route path="orcamentos/novo" element={<BudgetForm />} />
        <Route path="orcamentos/:id" element={<BudgetDetail />} />
        <Route path="orcamentos/:id/editar" element={<BudgetForm />} />
        <Route path="orcamentos/:id/lancamentos" element={<FdpEntriesDetail scope="admin" />} />
        <Route path="escolas" element={<SchoolsList />} />
        <Route path="escolas/nova" element={<SchoolForm />} />
        <Route path="escolas/:id" element={<SchoolForm />} />
        <Route path="solicitacoes" element={<ServiceRequests />} />
        <Route path="servicos" element={<ServicesItems />} />
        <Route path="anotacoes" element={<Anotacoes />} />
        <Route path="cronograma" element={<Cronograma />} />
        <Route path="calendario" element={<Calendario />} />
        <Route path="minha-conta" element={<MinhaConta />} />
        <Route path="funcionarios" element={<Funcionarios />} />
        <Route path="diarias" element={<Diarias />} />
        <Route path="funcionarios/diarias" element={<Diarias />} />
        <Route path="funcionarios/historico" element={<PayrollHistory />} />
        <Route path="relatorios" element={<Relatorios />} />
        <Route path="fdp" element={<FazDepoisPaga />} />
        <Route path="financeiro" element={<Financeiro />} />
        <Route path="financeiro/caixa" element={<FinanceCaixa />} />
        <Route path="financeiro/conta-dia" element={<FinanceContaDia />} />
        <Route path="financeiro/investimento-rafael" element={<RafaelInvest />} />
        <Route path="financeiro/mensal" element={<FinanceMonthly />} />
        <Route path="financeiro/impostos" element={<FinanceImpostos />} />
        <Route path="financeiro/cartao" element={<FinanceCartao />} />
        <Route path="notificacoes" element={<Notificacoes />} />
        <Route path="financeiro/socios" element={<SociosList />} />
        <Route path="financeiro/socios/:socioId" element={<SocioReport />} />
        <Route path="financeiro/configuracoes" element={<FinanceSettings />} />
        <Route path="configuracoes" element={<Settings />} />
        <Route path="landing-media" element={<LandingMedia />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <GlobalLoadingBar />
      <AppRouter />
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
